import {Evaluation, Ponderaciones} from "domain/entities/Evaluation";
import {EvaluationService, GuideType} from 'domain/ports';
import {API_BASE_URL, GUIDE_KEYS} from 'constants/app';
import {HttpWeightingService} from "infrastructure/services/HttpWeightingService";

/**
 * Implementación por defecto del servicio de evaluación
 */
export class DefaultEvaluationService implements EvaluationService {
    cachedWeightings: Record<GuideType, Ponderaciones> = {
        'GUIA_II': {} as Ponderaciones,
        'GUIA_III': {} as Ponderaciones
    };
    private isInitialized: boolean = false;
    private initPromise: Promise<void> | null = null;
    private httpWeightingService: HttpWeightingService;

    constructor(apiBaseUrl: string = API_BASE_URL) {
        this.httpWeightingService = new HttpWeightingService(apiBaseUrl);
    }

    async initializeGuideFromApi(guideKey: GuideType): Promise<void> {

        console.log("Ponderaciones", guideKey);
        if (this.isInitialized) {
            return;
        }



        this.isInitialized = true;
        this.initPromise = (async () => {
            try {
                const weightings = await this.httpWeightingService.getWeightings(guideKey);
                if (weightings) {
                    this.cachedWeightings[guideKey] = weightings;
                    console.log("Todas las ponderaciones", this.cachedWeightings);
                    console.log(`ponderaciones de ${guideKey} cargada correctamente`);
                } else {
                    delete this.cachedWeightings[guideKey];
                    console.warn(`No se recibieron ponderaciones para ${guideKey}`);
                    throw new Error(`Ponderaciones vacías para ${guideKey}`);
                }
            } catch (error) {
                console.error('Error inicializando el repositorio de ponderaciones desde la API:', error);
                this.initPromise = null;
                this.isInitialized = false;
                throw error;
            } finally {
                this.isInitialized = false;
            }
        })();

        return this.initPromise;
    }

    /**
     * Evalúa un valor según las reglas de ponderación
     * @param value Valor a evaluar
     * @param rules Reglas de evaluación
     * @returns Calificación correspondiente al valor
     */
    evaluateValue(value: number, rules: Evaluation[]): string {
        for (const rule of rules) {
            if (rule.upperLimit != null) {
                if (value >= rule.lowerLimit && value <= rule.upperLimit) {
                    return rule.rating;
                }
            } else {
                if (value >= rule.lowerLimit) {
                    return rule.rating;
                }
            }
        }
        return 'No evaluado';
    }

    /**
     * Obtiene las ponderaciones para un formulario específico
     * @param formKey Clave del formulario (GUIDE_KEYS)
     * @returns Ponderaciones para el formulario
     */
    async getPonderaciones(formKey: string): Promise<Ponderaciones | null> {
        if (formKey === GUIDE_KEYS.II) {
            return this.cachedWeightings[GUIDE_KEYS.II];
        } else if (formKey === GUIDE_KEYS.III) {
            return this.cachedWeightings[GUIDE_KEYS.III];
        }
        return null;
    }

    /**
     * Evalúa los totales de categorías según las ponderaciones
     * @param categoryTotals Totales por categoría
     * @param ponderaciones Ponderaciones a aplicar
     * @returns Evaluaciones por categoría
     */
    evaluateCategories(
        categoryTotals: { [category: string]: number },
        ponderaciones: Ponderaciones
    ): { [key: string]: string } {
        const categoriesEvaluations: { [key: string]: string } = {};

        for (const category of ponderaciones.categories) {
            const categoryName = category.category;
            const categoryTotal = Object.entries(categoryTotals).find(([name]) =>
                name.toLowerCase() === categoryName.toLowerCase() ||
                name.includes(categoryName) ||
                categoryName.includes(name)
            );

            if (categoryTotal) {
                categoriesEvaluations[categoryTotal[0]] = this.evaluateValue(categoryTotal[1], category.rules);
            }
        }

        return categoriesEvaluations;
    }

    /**
     * Evalúa los totales de dominios según las ponderaciones
     * @param domainTotals Totales por dominio
     * @param ponderaciones Ponderaciones a aplicar
     * @returns Evaluaciones por dominio
     */
    evaluateDomains(
        domainTotals: { [domain: string]: number },
        ponderaciones: Ponderaciones
    ): { [key: string]: string } {
        const domainsEvaluations: { [key: string]: string } = {};

        for (const domain of ponderaciones.domains) {
            const domainName = domain.category;
            const domainTotal = Object.entries(domainTotals).find(([name]) =>
                name.toLowerCase() === domainName.toLowerCase() ||
                name.includes(domainName) ||
                domainName.includes(name)
            );

            if (domainTotal) {
                domainsEvaluations[domainTotal[0]] = this.evaluateValue(domainTotal[1], domain.rules);
            }
        }

        return domainsEvaluations;
    }

    /**
     * Evalúa el total general según las ponderaciones
     * @param totalSum Suma total de puntos
     * @param ponderaciones Ponderaciones a aplicar
     * @returns Evaluación final
     */
    evaluateFinal(
        totalSum: number,
        ponderaciones: Ponderaciones
    ): string {
        if (!ponderaciones || !ponderaciones.finalWeighting || !ponderaciones.finalWeighting.rules) {
            console.warn('No se encontraron reglas de ponderación final');
            return 'No evaluado';
        }
        return this.evaluateValue(totalSum, ponderaciones.finalWeighting.rules);
    }
}
